import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, BookImage, Check, ChevronDown, Expand, MessageCircle, Pencil, Send, Trash2, Upload, X } from "lucide-react";
import { Link } from "react-router-dom";
import MemberGate from "../components/MemberGate";
import { ErrorMessage, Loading } from "../components/Loading";
import { useApi } from "../hooks/useApi";
import { api } from "../lib/api";

function ArchiveLightbox({ photo, onClose }) {
  useEffect(() => {
    function closeOnEscape(event) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  return (
    <div className="college-days-lightbox" role="dialog" aria-modal="true" aria-label="College Days photograph" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <button className="icon-button" type="button" onClick={onClose} aria-label="Close photograph"><X aria-hidden="true" /></button>
      <figure>
        <img src={photo.src} alt={photo.title || photo.description || photo.originalAlt} />
        <figcaption>{photo.title || photo.description || photo.originalAlt}</figcaption>
      </figure>
    </div>
  );
}

const commentDate = (value) => new Intl.DateTimeFormat("en", {
  month: "short",
  day: "numeric",
  year: "numeric",
}).format(new Date(value));

function ArchiveEntry({ photo, index, canContribute, isAdministrator, onReload, onOpen }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(photo.title || "");
  const [description, setDescription] = useState(photo.description || "");
  const [location, setLocation] = useState(photo.location || "");
  const [year, setYear] = useState(photo.year || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [commenting, setCommenting] = useState(false);
  const [removingComment, setRemovingComment] = useState("");

  async function save(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api(`/api/college-days/${encodeURIComponent(photo.id)}`, {
        method: "PATCH",
        body: JSON.stringify({ title, description, location, year: year || null }),
      });
      setEditing(false);
      await onReload();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    const directUpload = photo.sourceAlbumId === "college-days-uploads";
    const prompt = directUpload
      ? "Delete this photograph from College Days?"
      : `Return this photograph to ${photo.sourceAlbumTitle || "its original reunion album"}?`;
    if (!window.confirm(prompt)) return;
    setSaving(true);
    setError("");
    try {
      await api(`/api/admin/college-days/${encodeURIComponent(photo.id)}`, { method: "DELETE" });
      await onReload();
    } catch (requestError) {
      setError(requestError.message);
      setSaving(false);
    }
  }

  async function addComment(event) {
    event.preventDefault();
    setCommenting(true);
    setError("");
    try {
      await api(`/api/college-days/${encodeURIComponent(photo.id)}/comments`, {
        method: "POST",
        body: JSON.stringify({ body: comment }),
      });
      setComment("");
      await onReload();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setCommenting(false);
    }
  }

  async function removeComment(item) {
    if (!window.confirm("Remove this comment?")) return;
    setRemovingComment(item.id);
    setError("");
    try {
      await api(`/api/college-days/${encodeURIComponent(photo.id)}/comments/${item.id}`, { method: "DELETE" });
      await onReload();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setRemovingComment("");
    }
  }

  return (
    <article className="college-days-entry" style={{ "--archive-index": index }}>
      <div className="college-days-entry__number" aria-hidden="true">{String(index + 1).padStart(2, "0")}</div>
      <button className="college-days-entry__image" type="button" onClick={() => onOpen(photo)} aria-label={`Open photograph ${index + 1}`}>
        <img src={photo.src} alt={photo.title || photo.description || photo.originalAlt} loading={index > 1 ? "lazy" : "eager"} />
        <span><Expand aria-hidden="true" /> View full frame</span>
      </button>
      <div className="college-days-entry__story">
        <p className="kicker">From {photo.sourceAlbumTitle || "the class archive"}</p>
        {editing ? (
          <form className="college-days-note-form" onSubmit={save}>
            <div className="college-days-note-form__metadata">
              <label className="form-field">Photograph title
                <input maxLength="160" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Give this frame a memorable title" autoFocus />
              </label>
              <label className="form-field">Location
                <input maxLength="180" value={location} onChange={(event) => setLocation(event.target.value)} placeholder="Campus, city, or venue" />
              </label>
              <label className="form-field">Year
                <input type="number" min="1900" max="2100" inputMode="numeric" value={year} onChange={(event) => setYear(event.target.value)} placeholder="1995" />
              </label>
            </div>
            <label className="form-field">Photograph description
              <textarea rows="7" maxLength="1200" value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Who is here? What was happening? What do you remember?" />
              <small>{description.length} / 1200 characters</small>
            </label>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            <div className="button-row">
              <button className="button button--ink" type="submit" disabled={saving}>{saving ? "Saving…" : "Save description"} <Check aria-hidden="true" /></button>
              <button className="button button--ghost" type="button" onClick={() => { setTitle(photo.title || ""); setDescription(photo.description || ""); setLocation(photo.location || ""); setYear(photo.year || ""); setEditing(false); setError(""); }} disabled={saving}>Cancel</button>
            </div>
          </form>
        ) : (
          <>
            <h3>{photo.title || photo.originalAlt || "Untitled photograph"}</h3>
            {photo.description && <blockquote>{photo.description}</blockquote>}
            {(photo.location || photo.year) && <p className="college-days-entry__place">{[photo.location, photo.year].filter(Boolean).join(" · ")}</p>}
            <p className="college-days-entry__date">Published {new Date(photo.createdAt).toLocaleDateString("en", { year: "numeric", month: "long", day: "numeric" })}</p>
            {(canContribute || isAdministrator) && (
              <div className="college-days-entry__admin">
                {canContribute && <button className="text-link" type="button" onClick={() => setEditing(true)}><Pencil aria-hidden="true" /> Edit story & context</button>}
                {isAdministrator && <button className="text-link is-danger" type="button" onClick={remove} disabled={saving}><Trash2 aria-hidden="true" /> {photo.sourceAlbumId === "college-days-uploads" ? "Delete photograph" : "Return to reunion album"}</button>}
              </div>
            )}
            {error && <ErrorMessage>{error}</ErrorMessage>}
          </>
        )}

        <section className={commentsOpen ? "college-days-comments is-open" : "college-days-comments"}>
          <button
            className="college-days-comments__toggle"
            type="button"
            aria-expanded={commentsOpen}
            aria-controls={`college-days-comments-${photo.id}`}
            onClick={() => setCommentsOpen((value) => !value)}
          >
            <span><MessageCircle aria-hidden="true" /> {photo.comments?.length || 0} {(photo.comments?.length || 0) === 1 ? "comment" : "comments"}</span>
            <span>{commentsOpen ? "Hide" : "View"} <ChevronDown aria-hidden="true" /></span>
          </button>
          {commentsOpen && (
            <div className="college-days-comments__panel" id={`college-days-comments-${photo.id}`}>
              {canContribute && (
                <form className="college-days-comment-form" onSubmit={addComment}>
                  <label htmlFor={`college-days-comment-${photo.id}`}>Add what this photograph brings back</label>
                  <textarea
                    id={`college-days-comment-${photo.id}`}
                    required
                    maxLength="1200"
                    rows="3"
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                    placeholder="Share a detail, a name, or a memory…"
                  />
                  <div>
                    <small>{comment.length} / 1,200</small>
                    <button className="button button--small button--ink" type="submit" disabled={commenting || !comment.trim()}>{commenting ? "Posting…" : "Post comment"} <Send aria-hidden="true" /></button>
                  </div>
                </form>
              )}
              {(photo.comments?.length || 0) === 0 ? (
                <p className="college-days-comments__empty">No comments yet.</p>
              ) : (
                <div className="college-days-comment-list">
                  {photo.comments.map((item) => (
                    <article className="college-days-comment" key={item.id}>
                      <p>{item.body}</p>
                      <footer>
                        <span><strong>{item.author.name}</strong> · {commentDate(item.createdAt)}</span>
                        {item.canDelete && <button type="button" onClick={() => removeComment(item)} disabled={removingComment === item.id}><Trash2 aria-hidden="true" /> {removingComment === item.id ? "Removing…" : "Remove"}</button>}
                      </footer>
                    </article>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </article>
  );
}

export default function CollegeDays({ session, onLogin, isAdministrator }) {
  const authenticated = Boolean(session?.authenticated);
  const canContribute = Number(session?.user?.memberId) > 0;
  const loader = useCallback(
    () => authenticated ? api("/api/college-days") : Promise.resolve({ photos: [] }),
    [authenticated],
  );
  const { data, error, loading, reload } = useApi(loader, [loader]);
  const [openPhoto, setOpenPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const photos = data?.photos || [];

  async function uploadPhotos(event) {
    const files = [...(event.target.files || [])];
    if (!files.length) return;
    const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
    if (files.length > 8 || totalBytes > 24 * 1024 * 1024 || files.some((file) => file.size > 8 * 1024 * 1024)) {
      setUploadError("Choose up to 8 photographs, each no larger than 8 MB and totaling no more than 24 MB.");
      event.target.value = "";
      return;
    }
    setUploading(true);
    setUploadError("");
    const body = new FormData();
    files.forEach((file) => body.append("photos", file));
    try {
      await api("/api/college-days/photos", { method: "POST", body });
      await reload();
    } catch (requestError) {
      setUploadError(requestError.message);
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  }

  if (!authenticated) {
    return <div className="college-days-gate section-wrap"><MemberGate onSuccess={onLogin} /></div>;
  }

  return (
    <>
      <section className="college-days-hero">
        <div className="college-days-hero__years" aria-hidden="true"><span>1991</span><i>—</i><span>95</span></div>
        <div className="college-days-hero__copy section-wrap">
          <p className="kicker">The photographs before the reunions</p>
          <h1>College<br /><em>Days.</em></h1>
          <p>Classrooms, corridors, tours, hostels, festivals, and the unplanned frames that became part of our shared history.</p>
          <div className="college-days-hero__actions">
            <span>{photos.length} {photos.length === 1 ? "archived photograph" : "archived photographs"}</span>
            {canContribute && (
              <label className="button button--yellow college-days-upload">
                <Upload aria-hidden="true" /> {uploading ? "Adding photographs…" : "Add photographs"}
                <input type="file" accept="image/jpeg,image/png,image/webp" multiple disabled={uploading} onChange={uploadPhotos} />
              </label>
            )}
          </div>
        </div>
      </section>

      <section className="college-days-archive section-wrap" aria-live="polite">
        <div className="college-days-archive__heading">
          <div><p className="kicker">Curated by the class</p><h2>Every frame carries a story.</h2></div>
          {canContribute ? (
            <p><BookImage aria-hidden="true" /> Add photographs here, then use <b>Edit story &amp; context</b> to identify people, places, and the memory behind each frame.</p>
          ) : (
            <p><BookImage aria-hidden="true" /> Shared-passphrase access is read-only. Stories and comments from classmates remain here with every photograph.</p>
          )}
        </div>
        {uploadError && <ErrorMessage>{uploadError}</ErrorMessage>}
        {loading && <Loading label="Opening the College Days archive" />}
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {!loading && !error && photos.length === 0 && (
          <div className="college-days-empty">
            <BookImage aria-hidden="true" />
            <p className="kicker">The first page is waiting</p>
            <h2>Bring the college years back into view.</h2>
            <p>{canContribute ? "Add a college photograph here, or visit a reunion album and bring one of its college-era frames into this archive." : "Classmates are curating photographs and the stories behind them. Check back as the college years return, one frame at a time."}</p>
            {canContribute ? (
              <label className="button button--ink college-days-upload">
                <Upload aria-hidden="true" /> {uploading ? "Adding photographs…" : "Add the first photographs"}
                <input type="file" accept="image/jpeg,image/png,image/webp" multiple disabled={uploading} onChange={uploadPhotos} />
              </label>
            ) : (
              <Link className="button button--ink" to="/reunions"><ArrowLeft aria-hidden="true" /> Browse the reunions</Link>
            )}
          </div>
        )}
        {photos.map((photo, index) => <ArchiveEntry key={photo.id} photo={photo} index={index} canContribute={canContribute} isAdministrator={isAdministrator} onReload={reload} onOpen={setOpenPhoto} />)}
      </section>

      {openPhoto && <ArchiveLightbox photo={openPhoto} onClose={() => setOpenPhoto(null)} />}
    </>
  );
}
